"use client";

import Link from "next/link";
import { useState, useEffect } from "react";
import logo from "@/asset/images/logo-mobile.png";
import Image from "next/image";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsOpen(false); // Close mobile menu after clicking
    }
  };

  // Track scroll position to change header appearance
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when window is resized to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isOpen]);

  const navItems = [
    { name: "About", id: "about" }, // Index of the About section in your slides array
    { name: "Services", id: "services" }, // Index of the Services section
    { name: "Cases", id: "cases" }, // Index for ShowcaseSection
    { name: "Contact", id: "contact" }, // Index for ContactSection
  ];

  return (
    <header
      className={`z-50 w-full transition-all duration-300 fixed top-0 left-0 right-0 ${
        scrolled ? "bg-white shadow-md py-2" : "bg-white/95 py-3"
      }`}>
      <nav className='mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='flex justify-end md:justify-center items-center h-10 md:h-12'>
          {/* Desktop Menu */}
          <div className='hidden md:flex items-center justify-center gap-6 lg:gap-[85px]'>
            {navItems.map((item) => (
              <Link
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => handleClick(e, item.id)}
                className='text-textColor hover:text-primary px-3 py-2 text-lg lg:text-xl font-medium transition-colors duration-200 cursor-pointer'>
                {item.name}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <div className='md:hidden flex items-center justify-between w-full'>
            <Link href='#home'>
              <Image src={logo.src} alt='logo' width={100} height={100} />
            </Link>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className='text-textColor hover:text-primary p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50'
              aria-label='Toggle menu'
              aria-expanded={isOpen}>
              <svg
                className='h-6 w-6'
                fill='none'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                viewBox='0 0 24 24'
                stroke='currentColor'>
                {isOpen ? (
                  <path d='M6 18L18 6M6 6l12 12' />
                ) : (
                  <path d='M4 6h16M4 12h16M4 18h16' />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isOpen ? "max-h-60 opacity-100" : "max-h-0 opacity-0"
          }`}>
          <div className='flex flex-col space-y-2 py-4'>
            {navItems.map((item) => (
              <Link
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => handleClick(e, item.id)}
                className='text-textColor hover:text-primary px-3 py-2 text-lg font-medium transition-colors duration-200 cursor-pointer'>
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
